import React, { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Upload, Sparkles, Loader2, Check, QrCode, Smartphone, Search, TriangleAlert } from "lucide-react";
import api from "@/lib/api";
import { compressImage, formatBytes } from "@/lib/imageCompress";
import { useI18n } from "@/contexts/I18nContext";
import { formatName, titleCaseName, lookupSwissPlz } from "@/lib/format";
import { toast } from "sonner";

const QUELLE_OPTIONS = [
  "Mitarbeiterempfehlung",
  "Kundenempfehlung",
  "Socialmedia Kampagne",
  "Messe",
  "Wettbewerb",
  "Mundpropaganda",
  "Umfrage",
  "Steuern",
  "Bestandskunde",
  "Bestandskunde gleicher Haushalt",
  "Eigenkreis",
  "Inbound-Call",
];

const empty = {
  client_type: "person",
  // Herkunft (must be answered before anything else)
  quelle: "", erstkontakt_durch: "",
  // Business fields (only used when client_type === "business")
  company_name: "", company_legal_form: "", company_start_date: "", company_uid: "",
  // Person / contact person fields
  first_name: "", last_name: "", email: "", phone: "",
  date_of_birth: "", place_of_birth: "", nationality: "",
  document_number: "", address: "", city: "", postal_code: "",
  canton: "", country: "CH", civil_status: "", civil_status_date: "", notes: "",
};

const LEGAL_FORMS = [
  { value: "Einzelunternehmen",        label: "Einzelunternehmen" },
  { value: "Kollektivgesellschaft",    label: "Kollektivgesellschaft (KlG)" },
  { value: "Kommanditgesellschaft",    label: "Kommanditgesellschaft (KmG)" },
  { value: "GmbH",                     label: "Gesellschaft mit beschränkter Haftung (GmbH)" },
  { value: "AG",                       label: "Aktiengesellschaft (AG)" },
  { value: "Genossenschaft",           label: "Genossenschaft" },
  { value: "Verein",                   label: "Verein" },
  { value: "Stiftung",                 label: "Stiftung" },
];

export default function NewClientDialog({ open, onOpenChange, aiMode = false, onCreated }) {
  const { t } = useI18n();
  const [form, setForm] = useState(empty);
  const [aiProvider, setAiProvider] = useState("claude");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiDone, setAiDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadedDocId, setUploadedDocId] = useState(null);
  const [dups, setDups] = useState([]);
  const [dupChecked, setDupChecked] = useState(false);
  const fileRef = useRef(null);

  const gateOk = !!form.quelle && !!form.erstkontakt_durch;

  // Mobile QR scan
  const [qrOpen, setQrOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [qrStatus, setQrStatus] = useState("idle"); // idle | waiting | uploaded | expired
  const pollRef = useRef(null);

  useEffect(() => {
    if (open) { setForm(empty); setAiDone(false); setDups([]); setDupChecked(false); }
  }, [open]);

  // Duplicate auto-check (debounced) whenever both names are present
  useEffect(() => {
    const fn = (form.first_name || "").trim();
    const ln = (form.last_name || "").trim();
    if (!fn || !ln) { setDups([]); setDupChecked(false); return; }
    const tmo = setTimeout(() => { checkDuplicates(false); }, 700);
    return () => clearTimeout(tmo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.first_name, form.last_name]);

  const checkDuplicates = async (manual = true) => {
    try {
      const r = await api.get("/clients/check-duplicate", {
        params: { first_name: form.first_name || "", last_name: form.last_name || "" },
      });
      setDups(r.data?.matches || []);
      setDupChecked(true);
      if (manual && (r.data?.matches || []).length === 0) toast.success("Kein Duplikat gefunden");
    } catch { /* ignore */ }
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Run AI extraction on an already-uploaded document_id (used by both desktop and mobile flows)
  const runAiExtract = async (documentId) => {
    setAiBusy(true);
    setAiDone(false);
    try {
      const ext = await api.post("/ai/extract-identity", {
        document_id: documentId,
        provider: aiProvider,
      });
      const d = ext.data || {};
      setForm(f => ({
        ...f,
        first_name: d.first_name || f.first_name,
        last_name: d.last_name || f.last_name,
        date_of_birth: d.date_of_birth || f.date_of_birth,
        place_of_birth: d.place_of_birth || f.place_of_birth,
        nationality: d.nationality || f.nationality,
        document_number: d.document_number || f.document_number,
        permit_type: d.permit_type || f.permit_type,
        address: d.address || f.address,
        city: d.city || f.city,
        postal_code: d.postal_code || f.postal_code,
      }));
      setAiDone(true);
      toast.success(t.extraction_done);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI extraction failed");
    } finally {
      setAiBusy(false);
    }
  };

  // ---------- Mobile QR scan flow ----------
  const startQrSession = async () => {
    setQrOpen(true);
    setQrStatus("waiting");
    try {
      const res = await api.post("/upload-sessions", { purpose: "identity" });
      setSession(res.data);
      // poll every 2s
      pollRef.current = setInterval(async () => {
        try {
          const s = await api.get(`/upload-sessions/${res.data.id}`);
          if (s.data.status === "uploaded" && s.data.document_id) {
            clearInterval(pollRef.current);
            setQrStatus("uploaded");
            toast.success("Dokument vom Telefon empfangen");
            setQrOpen(false);
            setUploadedDocId(s.data.document_id);
            await runAiExtract(s.data.document_id);
          }
        } catch { /* ignore */ }
      }, 2000);
    } catch (err) {
      toast.error("Impossibile creare la sessione");
      setQrOpen(false);
    }
  };

  const stopQrSession = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setQrOpen(false);
    setSession(null);
    setQrStatus("idle");
  };

  // Auto-lookup CH city + canton when PLZ is 4 digits
  useEffect(() => {
    const plz = (form.postal_code || "").trim();
    if (!/^\d{4}$/.test(plz)) return;
    let cancelled = false;
    lookupSwissPlz(plz).then(r => {
      if (cancelled || !r) return;
      setForm(f => ({
        ...f,
        city: f.city || r.city,
        canton: f.canton || r.canton_abbr || r.canton,
      }));
    });
    return () => { cancelled = true; };
  }, [form.postal_code]);

  // Upload an already-selected File (image/PDF), then run AI extraction.
  // Used by file-picker, mobile QR scan, and ID-Camera-Mask capture.
  const uploadAndExtract = async (file) => {
    if (!file) return;
    setAiBusy(true);
    setAiDone(false);
    try {
      // Compress image (PDFs pass through compressImage unchanged)
      const compressed = await compressImage(file, { maxWidth: 1600, maxHeight: 1600, quality: 0.85 });
      if (compressed !== file && compressed.size < file.size) {
        const pct = Math.round(100 - (compressed.size * 100) / file.size);
        toast.info(`Bild optimiert: ${formatBytes(file.size)} → ${formatBytes(compressed.size)} (−${pct}%)`);
      }

      // Upload + AI extraction
      const fd = new FormData();
      fd.append("file", compressed);
      const up = await api.post("/documents/upload-anonymous", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedDocId(up.data.id);
      await runAiExtract(up.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI extraction failed");
      setAiBusy(false);
    }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    await uploadAndExtract(file);
  };

  // Upload SENZA AI: carica il documento ma lascia compilare i campi manualmente.
  // Utile quando l'AI non è disponibile o per ID con formato atipico.
  const onPickFileNoAi = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setAiBusy(true); setAiDone(false);
    try {
      const compressed = await compressImage(file, { maxWidth: 1600, maxHeight: 1600, quality: 0.85 });
      const fd = new FormData();
      fd.append("file", compressed);
      const up = await api.post("/documents/upload-anonymous", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedDocId(up.data.id);
      setAiDone(true);
      toast.success("Dokument hochgeladen · Felder manuell ausfüllen");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setAiBusy(false);
    }
  };

  const onSave = async () => {
    if (!form.quelle || !form.erstkontakt_durch) {
      toast.error("Bitte zuerst Quelle und Erstkontakt auswählen");
      return;
    }
    if (dups.some(d => d.exact)) {
      toast.error("Kunde existiert bereits — kein Duplikat erfassen");
      return;
    }
    if (form.client_type === "business") {
      if (!form.company_name?.trim()) {
        toast.error("Firmenname obbligatorio per Business");
        return;
      }
      if (!form.company_legal_form) {
        toast.error("Rechtsform obbligatoria per Business");
        return;
      }
    }
    if (!form.first_name && !form.last_name) {
      toast.error(form.client_type === "business" ? "Ansprechperson (Vor/Nachname) obbligatori" : "Vorname / Nachname obbligatori");
      return;
    }
    const normalized = {
      ...form,
      first_name: titleCaseName(form.first_name),
      last_name: titleCaseName(form.last_name),
      place_of_birth: titleCaseName(form.place_of_birth || ""),
      city: titleCaseName(form.city || ""),
      nationality: titleCaseName(form.nationality || ""),
      country: titleCaseName(form.country || ""),
      address: titleCaseName(form.address || ""),
    };
    setSaving(true);
    try {
      const res = await api.post("/clients", normalized);
      // Attach the ID document used for AI extraction to the new client
      if (uploadedDocId && res.data?.id) {
        try {
          await api.patch(`/documents/${uploadedDocId}/attach-client`, null, {
            params: { client_id: res.data.id, doc_type: "carta_identita" },
          });
        } catch (e) { /* non-fatal */ }
      }
      toast.success("Kunde erstellt");
      onOpenChange(false);
      onCreated && onCreated(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally { setSaving(false); }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl rounded-none p-0 overflow-hidden">
        <DialogHeader className="px-6 py-4 border-b border-border">
          <div className="label-uppercase text-muted-foreground">{aiMode ? "AI assisted" : "Manual"}</div>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {aiMode ? t.add_client_ai : t.add_client}
          </DialogTitle>
        </DialogHeader>

        <div className="p-6 max-h-[70vh] overflow-auto">
          {/* HERKUNFT — must be answered before anything else */}
          <div className="prime-card p-4 sm:p-5 mb-6 border-prime/40" data-testid="herkunft-section">
            <div className="label-uppercase text-prime mb-1 flex items-center gap-1.5">
              <span className="w-1 h-3 bg-prime inline-block" /> Herkunft des Kunden
            </div>
            <p className="text-xs text-muted-foreground mb-3">Wie hat der Kunde von uns erfahren? (Pflicht vor der Erfassung)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="label-uppercase text-muted-foreground">Quelle *</Label>
                <Select value={form.quelle || ""} onValueChange={v => update("quelle", v)}>
                  <SelectTrigger className="mt-2 h-10" data-testid="f-quelle">
                    <SelectValue placeholder="Bitte wählen…" />
                  </SelectTrigger>
                  <SelectContent>
                    {QUELLE_OPTIONS.map(q => (
                      <SelectItem key={q} value={q}>{q}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-uppercase text-muted-foreground">Erstkontakt ausgeführt durch *</Label>
                <Select value={form.erstkontakt_durch || ""} onValueChange={v => update("erstkontakt_durch", v)}>
                  <SelectTrigger className="mt-2 h-10" data-testid="f-erstkontakt">
                    <SelectValue placeholder="Bitte wählen…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="berater">Berater</SelectItem>
                    <SelectItem value="kunde">Kunde</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="relative">
            {!gateOk && (
              <div className="absolute inset-0 z-10 bg-background/70 backdrop-blur-[2px] flex items-start justify-center pt-12 rounded" data-testid="herkunft-gate">
                <div className="text-xs font-semibold text-muted-foreground border border-border bg-card px-4 py-2.5 rounded shadow-sm">
                  Bitte zuerst <span className="text-prime">Quelle</span> und <span className="text-prime">Erstkontakt</span> auswählen
                </div>
              </div>
            )}

          {/* PERSON / BUSINESS TOGGLE */}
          <div className="mb-5">
            <Label className="label-uppercase text-muted-foreground mb-2 block">Kundentyp</Label>
            <div className="grid grid-cols-2 gap-2" data-testid="client-type-toggle">
              <button
                type="button"
                onClick={() => update("client_type", "person")}
                className={`px-4 py-3 rounded-md border-2 text-sm font-semibold transition-all ${
                  form.client_type !== "business"
                    ? "border-prime bg-prime/10 text-prime"
                    : "border-border bg-card hover:border-prime/40 text-muted-foreground"
                }`}
                data-testid="client-type-person"
              >
                Privatperson
              </button>
              <button
                type="button"
                onClick={() => update("client_type", "business")}
                className={`px-4 py-3 rounded-md border-2 text-sm font-semibold transition-all ${
                  form.client_type === "business"
                    ? "border-prime bg-prime/10 text-prime"
                    : "border-border bg-card hover:border-prime/40 text-muted-foreground"
                }`}
                data-testid="client-type-business"
              >
                <span className="inline-flex items-center gap-1.5">
                  Business
                  {form.client_type === "business" && (
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-prime text-white">Gold</span>
                  )}
                </span>
              </button>
            </div>
          </div>

          {/* BUSINESS SECTION */}
          {form.client_type === "business" && (
            <div className="prime-card p-4 sm:p-5 mb-6 border-prime/40" data-testid="business-section">
              <div className="label-uppercase text-prime mb-2 flex items-center gap-1.5">
                <span className="w-1 h-3 bg-prime inline-block" /> Firmenangaben
              </div>
              <h3 className="font-display font-bold text-base sm:text-lg mb-3">Unternehmensdaten</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field
                  label="Firmenname *"
                  value={form.company_name}
                  onChange={v => update("company_name", v)}
                  testId="f-company-name"
                />
                <div>
                  <Label className="label-uppercase text-muted-foreground">Rechtsform *</Label>
                  <Select value={form.company_legal_form || ""} onValueChange={v => update("company_legal_form", v)}>
                    <SelectTrigger className="mt-2 h-10" data-testid="f-company-legal-form">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {LEGAL_FORMS.map(lf => (
                        <SelectItem key={lf.value} value={lf.value}>{lf.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Field
                  label="Gründungsdatum"
                  type="date"
                  value={form.company_start_date}
                  onChange={v => update("company_start_date", v)}
                  testId="f-company-start-date"
                />
                <Field
                  label="UID-Nummer (P.IVA) — CHE-xxx.xxx.xxx"
                  value={form.company_uid}
                  onChange={v => update("company_uid", v)}
                  testId="f-company-uid"
                />
              </div>
              <div className="text-[10px] text-muted-foreground mt-3 italic">
                Die folgenden Felder (Vorname, Nachname, etc.) beziehen sich auf die <strong>Ansprechperson</strong> des Unternehmens.
              </div>
            </div>
          )}

          {aiMode && (
            <div className="prime-card p-4 sm:p-5 mb-6 relative overflow-hidden" data-testid="ai-extract-card">
              {aiBusy && <div className="absolute inset-x-0 bottom-0 h-1 ai-beam" />}
              {/* Heading */}
              <div className="mb-3">
                <div className="label-uppercase text-prime flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> AI Upload
                </div>
                <h3 className="font-display font-bold text-base sm:text-lg mt-1">Hochladen Carta d'Identità o Patente</h3>
                <p className="text-xs text-muted-foreground mt-1">{t.fill_review}</p>
              </div>
              {/* Provider selector + actions — stacked on mobile, inline on sm+ */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2">
                <Select value={aiProvider} onValueChange={setAiProvider}>
                  <SelectTrigger className="rounded-none border-foreground h-10 w-full sm:w-40" data-testid="ai-provider-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="claude">Claude Sonnet 4.6</SelectItem>
                    <SelectItem value="gpt">GPT-5.5</SelectItem>
                  </SelectContent>
                </Select>
                {/* 3 action buttons: AI · QR scan · Manual */}
                <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:flex-1">
                  <Button
                    asChild disabled={aiBusy}
                    className="rounded-none bg-prime hover:bg-prime-dark text-white gap-1.5 h-10 px-3 text-xs sm:text-sm flex-1 min-w-0"
                  >
                    <label htmlFor="ai-file-input" data-testid="ai-upload-btn" className={aiBusy ? "pointer-events-none opacity-60" : "cursor-pointer"}>
                      {aiBusy ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> :
                        aiDone ? <Check className="w-4 h-4 shrink-0" /> :
                        <Upload className="w-4 h-4 shrink-0" />}
                      <span className="truncate">{aiBusy ? t.processing_ai : aiDone ? t.extraction_done : t.upload}</span>
                    </label>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={startQrSession}
                    disabled={aiBusy}
                    className="rounded-none border-foreground gap-1.5 h-10 px-3 text-xs sm:text-sm flex-1 min-w-0"
                    title="Vom Telefon scannen"
                    data-testid="qr-scan-btn"
                  >
                    <QrCode className="w-4 h-4 shrink-0" />
                    <span className="truncate">Von telefono</span>
                  </Button>
                  <Button
                    asChild disabled={aiBusy}
                    variant="outline"
                    className="rounded-none gap-1.5 h-10 px-3 text-xs sm:text-sm flex-1 min-w-0 border-stone-300 text-stone-700 hover:bg-stone-50 col-span-2 sm:col-span-1"
                  >
                    <label
                      htmlFor="ai-file-input-noai"
                      data-testid="ai-upload-noai-btn"
                      className={aiBusy ? "pointer-events-none opacity-60" : "cursor-pointer"}
                      title="Dokument hochladen, aber Felder selbst ausfüllen (ohne KI)"
                    >
                      <Upload className="w-4 h-4 shrink-0" />
                      <span className="truncate">Manuell · ohne KI</span>
                    </label>
                  </Button>
                </div>
                <input
                  ref={fileRef} id="ai-file-input" type="file" className="hidden"
                  accept="image/*,application/pdf" onChange={onPickFile}
                  data-testid="ai-file-input"
                />
                <input
                  id="ai-file-input-noai" type="file" className="hidden"
                  accept="image/*,application/pdf" onChange={onPickFileNoAi}
                  data-testid="ai-file-input-noai"
                />
              </div>
            </div>
          )}

          {/* DUPLICATE CHECK */}
          {(form.first_name || "").trim() && (form.last_name || "").trim() && (
            <div className="mb-4">
              <div className="flex items-center gap-3">
                <Button
                  type="button" variant="outline" size="sm"
                  className="rounded-none h-8 text-xs gap-1.5"
                  onClick={() => checkDuplicates(true)}
                  data-testid="check-duplicate-btn"
                >
                  <Search className="w-3.5 h-3.5" /> Duplikat prüfen
                </Button>
                {dupChecked && dups.length === 0 && (
                  <span className="text-[11px] text-green-600 inline-flex items-center gap-1">
                    <Check className="w-3 h-3" /> Kein Duplikat gefunden
                  </span>
                )}
              </div>
              {dups.length > 0 && (
                <div
                  className={`mt-2 border rounded-md p-3 text-xs ${
                    dups.some(d => d.exact)
                      ? "border-red-400 bg-red-50 dark:bg-red-900/10"
                      : "border-amber-400 bg-amber-50 dark:bg-amber-900/10"
                  }`}
                  data-testid="duplicate-warning"
                >
                  <div className="font-bold flex items-center gap-1.5 mb-1">
                    <TriangleAlert className="w-3.5 h-3.5" />
                    {dups.some(d => d.exact)
                      ? "Kunde existiert bereits — Speichern blockiert"
                      : "Ähnliche Kunden gefunden — bitte prüfen"}
                  </div>
                  {dups.map(d => (
                    <div key={d.id} className="flex flex-wrap items-center gap-x-2 py-0.5">
                      <span className="font-semibold">{d.first_name} {d.last_name}</span>
                      {d.date_of_birth && <span className="text-muted-foreground">· {d.date_of_birth}</span>}
                      {d.city && <span className="text-muted-foreground">· {d.city}</span>}
                      {d.owner_name && <span className="text-muted-foreground">· Berater: {d.owner_name}</span>}
                      {d.exact && <span className="text-red-600 font-bold">EXAKT</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label={form.client_type === "business" ? "Ansprechperson · Vorname" : t.first_name}
              value={form.first_name}
              onChange={v => update("first_name", v)}
              testId="f-first-name"
            />
            <Field
              label={form.client_type === "business" ? "Ansprechperson · Nachname" : t.last_name}
              value={form.last_name}
              onChange={v => update("last_name", v)}
              testId="f-last-name"
            />
            <Field label={t.email} value={form.email} onChange={v => update("email", v)} type="email" testId="f-email" />
            <Field label={t.phone} value={form.phone} onChange={v => update("phone", v)} testId="f-phone" />
            <Field label={t.date_of_birth} value={form.date_of_birth} onChange={v => update("date_of_birth", v)} type="date" testId="f-dob" />
            <Field label={t.place_of_birth} value={form.place_of_birth} onChange={v => update("place_of_birth", v)} testId="f-pob" />
            <Field label={t.nationality} value={form.nationality} onChange={v => update("nationality", v)} testId="f-nat" />
            <Field label={t.document_number} value={form.document_number} onChange={v => update("document_number", v)} testId="f-docnum" />
            <div>
              <Label className="label-uppercase text-muted-foreground">{t.permit_type || "Typ Dokument/permesso"}</Label>
              <Select value={form.permit_type || ""} onValueChange={v => update("permit_type", v)}>
                <SelectTrigger className="mt-2 h-10" data-testid="f-permit-type">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CartaIdentita">Carta d'identità</SelectItem>
                  <SelectItem value="Führerausweis">Patente</SelectItem>
                  <SelectItem value="Reisepass">Passaporto</SelectItem>
                  <SelectItem value="B">Bewilligung B</SelectItem>
                  <SelectItem value="L">Bewilligung L</SelectItem>
                  <SelectItem value="C">Bewilligung C</SelectItem>
                  <SelectItem value="Swisspass">Swisspass</SelectItem>
                  <SelectItem value="Meldebestätigung">Meldebestätigung</SelectItem>
                  <SelectItem value="Other">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field label={t.address} value={form.address} onChange={v => update("address", v)} testId="f-addr" />
            <Field label={t.city} value={form.city} onChange={v => update("city", v)} testId="f-city" />
            <Field label={t.postal_code} value={form.postal_code} onChange={v => update("postal_code", v)} testId="f-zip" />
            <Field label={t.canton} value={form.canton} onChange={v => update("canton", v)} testId="f-canton" />
            <div>
              <Label className="label-uppercase text-muted-foreground">Zivilstand</Label>
              <Select value={form.civil_status || ""} onValueChange={v => update("civil_status", v)}>
                <SelectTrigger className="mt-2 h-10" data-testid="f-civil-status">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="celibe">Ledig</SelectItem>
                  <SelectItem value="sposato">Verheiratet</SelectItem>
                  <SelectItem value="separato">Getrennt</SelectItem>
                  <SelectItem value="divorziato">Geschieden</SelectItem>
                  <SelectItem value="vedovo">Verwitwet</SelectItem>
                  <SelectItem value="orfano">Waise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.civil_status && form.civil_status !== "celibe" && (
              <Field
                label={
                  form.civil_status === "sposato" ? "Hochzeitsdatum"
                    : form.civil_status === "separato" ? "Trennungsdatum"
                      : form.civil_status === "divorziato" ? "Scheidungsdatum"
                        : form.civil_status === "vedovo" ? "Datum Verwitwung"
                          : "Ereignisdatum"
                }
                value={form.civil_status_date}
                onChange={v => update("civil_status_date", v)}
                placeholder="GG/MM/AAAA"
                testId="f-civil-status-date"
              />
            )}
          </div>

          <div className="mt-4">
            <Label className="label-uppercase text-muted-foreground">{t.notes}</Label>
            <Textarea
              value={form.notes} onChange={e => update("notes", e.target.value)}
              className="rounded-none border-border focus-visible:ring-0 focus-visible:border-foreground mt-2"
              rows={2} data-testid="f-notes"
            />
          </div>
          </div>{/* /gated content */}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-border bg-secondary/40">
          <Button variant="ghost" className="rounded-none" onClick={() => onOpenChange(false)}>{t.cancel}</Button>
          <Button
            onClick={onSave} disabled={saving || !gateOk || dups.some(d => d.exact)}
            className="rounded-none bg-foreground text-background hover:bg-foreground/90"
            data-testid="save-client-btn"
          >
            {saving ? "…" : t.save}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* QR scan dialog */}
      <Dialog open={qrOpen} onOpenChange={(v) => { if (!v) stopQrSession(); }}>
        <DialogContent className="max-w-md rounded-none p-0 overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b border-border">
            <div className="label-uppercase text-prime flex items-center gap-1">
              <Smartphone className="w-3 h-3" /> Scan vom Telefon
            </div>
            <DialogTitle className="font-display text-2xl tracking-tight">Inquadra il QR</DialogTitle>
          </DialogHeader>
          <div className="p-6 text-center">
            {session ? (
              <>
                <div className="mx-auto bg-white p-3 rounded-lg inline-block border border-border">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=8&data=${encodeURIComponent(
                      `${window.location.origin}/m/u/${session.id}`,
                    )}`}
                    alt="QR code"
                    className="w-[280px] h-[280px] block"
                    data-testid="qr-image"
                  />
                </div>
                <div className="mt-5 flex items-center justify-center gap-2 text-sm">
                  {qrStatus === "waiting" ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin text-prime" />
                      <span className="text-muted-foreground">Warten del Dokument vom Telefon…</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 text-green-600" />
                      <span>Dokument ricevuto</span>
                    </>
                  )}
                </div>
                <ol className="text-xs text-muted-foreground mt-4 text-left max-w-xs mx-auto space-y-1">
                  <li>1. Öffnen la fotocamera del telefono</li>
                  <li>2. Inquadra il QR — si aprirà una pagina web</li>
                  <li>3. Scatta una foto del Dokument</li>
                  <li>4. L'IA compilerà automaticamente i campi</li>
                </ol>
                <div className="mt-3 text-[10px] text-muted-foreground font-mono break-all px-4">
                  {`${window.location.origin}/m/u/${session.id}`}
                </div>
              </>
            ) : (
              <div className="py-12">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
              </div>
            )}
          </div>
          <DialogFooter className="px-6 py-4 border-t border-border bg-secondary/40">
            <Button variant="ghost" className="rounded-none" onClick={stopQrSession} data-testid="qr-cancel-btn">
              Abbrechen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
    </>
  );
}

function Field({ label, value, onChange, type = "text", testId, placeholder }) {
  return (
    <div>
      <Label className="label-uppercase text-muted-foreground">{label}</Label>
      <Input
        type={type} value={value || ""} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-none border-border focus-visible:ring-0 focus-visible:border-foreground mt-2 h-10"
        data-testid={testId}
      />
    </div>
  );
}
